import React, { useEffect, useState } from 'react';

interface Service {
  id: number;
  title: string;
  description: string;
  icon: string;
  image: string;
}

export const ServicesCarousel: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const services: Service[] = [
    {
      id: 1,
      title: "AI Strategy Consulting",
      description: "Develop comprehensive AI roadmaps tailored to your business objectives, ensuring strategic alignment and maximum ROI from AI investments.",
      icon: "ri-brain-line",
      image: "https://public.readdy.ai/ai/img_res/7b491ec6a55ec6a4e5329dff2a67ad1a.jpg"
    },
    {
      id: 2,
      title: "Process Automation",
      description: "Streamline operations with intelligent automation solutions that reduce costs, eliminate errors, and accelerate business processes.",
      icon: "ri-settings-3-line",
      image: "https://readdy.ai/api/search-image?query=Automated%20robotic%20process%20automation%20in%20modern%20office%20environment%2C%20intelligent%20workflow%20systems%2C%20digital%20process%20optimization%2C%20automated%20business%20operations%2C%20futuristic%20workplace%20technology%2C%20clean%20minimalist%20design&width=400&height=300&seq=automation-001&orientation=landscape"
    },
    {
      id: 3,
      title: "Machine Learning Solutions",
      description: "Custom ML models and algorithms that unlock insights from your data, enabling predictive analytics and intelligent decision-making.",
      icon: "ri-cpu-line",
      image: "https://readdy.ai/api/search-image?query=Data%20scientist%20working%20with%20machine%20learning%20algorithms%20on%20multiple%20monitors%2C%20complex%20data%20visualization%2C%20neural%20network%20diagrams%2C%20artificial%20intelligence%20development%2C%20modern%20tech%20workspace%2C%20professional%20analytics%20environment&width=400&height=300&seq=ml-solutions-001&orientation=landscape"
    },
    {
      id: 4,
      title: "Digital Transformation",
      description: "End-to-end digital transformation services that modernize your technology stack and revolutionize customer experiences.",
      icon: "ri-rocket-line",
      image: "https://readdy.ai/api/search-image?query=Digital%20transformation%20concept%20with%20cloud%20computing%2C%20mobile%20technology%2C%20IoT%20devices%2C%20modern%20business%20transformation%2C%20connected%20digital%20ecosystem%2C%20innovative%20technology%20solutions%2C%20clean%20professional%20design&width=400&height=300&seq=digital-transform-001&orientation=landscape"
    },
    {
      id: 5,
      title: "AI Implementation",
      description: "Seamless integration of AI technologies into existing systems with comprehensive training and ongoing support for your team.",
      icon: "ri-code-s-slash-line",
      image: "https://readdy.ai/api/search-image?query=Software%20engineers%20implementing%20AI%20systems%2C%20coding%20artificial%20intelligence%20solutions%2C%20technology%20integration%20process%2C%20modern%20development%20environment%2C%20collaborative%20programming%20team%2C%20professional%20tech%20workspace&width=400&height=300&seq=ai-implementation-001&orientation=landscape"
    },
    {
      id: 6,
      title: "Data Analytics & Insights",
      description: "Transform raw data into actionable business intelligence with advanced analytics platforms and real-time reporting dashboards.",
      icon: "ri-bar-chart-line",
      image: "https://readdy.ai/api/search-image?query=Business%20intelligence%20dashboard%20with%20data%20analytics%2C%20real-time%20reporting%20charts%2C%20data%20visualization%20screens%2C%20business%20metrics%20analysis%2C%20modern%20analytics%20workspace%2C%20professional%20data%20science%20environment&width=400&height=300&seq=data-analytics-001&orientation=landscape"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % services.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [services.length]);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % services.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + services.length) % services.length);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Our <span className="text-blue-600">AI Services</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive AI and automation solutions designed to transform your business operations and drive sustainable growth
          </p>
        </div>

        <div className="relative">
          {/* Carousel Container */}
          <div className="overflow-hidden rounded-2xl shadow-2xl bg-white">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {services.map((service) => (
                <div key={service.id} className="w-full flex-shrink-0">
                  <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[500px]">
                    {/* Content Side */}
                    <div className="p-12 flex flex-col justify-center bg-white">
                      <div className="mb-6">
                        <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mb-6">
                          <i className={`${service.icon} text-2xl text-blue-600`}></i>
                        </div>
                        <h3 className="text-3xl font-bold text-gray-900 mb-4">
                          {service.title}
                        </h3>
                        <p className="text-lg text-gray-600 leading-relaxed">
                          {service.description}
                        </p>
                      </div>
                      <div className="flex items-center space-x-4">
                        <a 
                          href="/ai-consulting"
                          className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          Learn More
                        </a>
                        <a 
                          href="/ai-consulting"
                          className="text-blue-600 hover:text-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          Get Started →
                        </a>
                      </div>
                    </div>

                    {/* Image Side */}
                    <div className="relative bg-gradient-to-br from-blue-600 to-indigo-700">
                      <img 
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover object-center"
                      />
                      <div className="absolute inset-0 bg-blue-900/20"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button 
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/90 hover:bg-white rounded-full shadow-lg transition-all cursor-pointer z-10"
          >
            <i className="ri-arrow-left-line text-xl text-gray-700"></i>
          </button>
          <button 
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/90 hover:bg-white rounded-full shadow-lg transition-all cursor-pointer z-10"
          >
            <i className="ri-arrow-right-line text-xl text-gray-700"></i>
          </button>

          {/* Dots Indicator */}
          <div className="flex justify-center mt-8 space-x-2">
            {services.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all cursor-pointer ${
                  index === currentIndex 
                    ? 'bg-blue-600 w-8' 
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Service Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
          <div className="text-center">
            <div className="text-4xl font-bold text-blue-600 mb-2">500+</div>
            <div className="text-gray-600">AI Projects Delivered</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-blue-600 mb-2">95%</div>
            <div className="text-gray-600">Client Satisfaction Rate</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-blue-600 mb-2">24/7</div>
            <div className="text-gray-600">Support & Monitoring</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesCarousel;
